import type { User } from '@/lib/pocketbase'
import { FaBook, FaNewspaper, FaEdit, FaShoppingCart } from 'react-icons/fa'
import { useUserStats } from '@/queries/profileQueries'
import { useKixBalance } from '@/queries/kixQueries'
import { getFileUrl, generateShopSSOToken } from '@/lib/pocketbase'
import { useState } from 'react'

interface ProfileHeaderProps {
  user: User
  onEditProfile?: () => void
  onEditAvatar?: () => void
}

const ProfileHeader = ({ user, onEditAvatar }: ProfileHeaderProps) => {
  const { data: stats } = useUserStats()
  const { data: kix } = useKixBalance()
  const [isLoadingSSO, setIsLoadingSSO] = useState(false)

  const avatarUrl = user.avatar ? getFileUrl('users', user.id, user.avatar) : null

  const handleGoToShop = async () => {
    try {
      setIsLoadingSSO(true)
      const redirectUrl = await generateShopSSOToken()
      window.location.href = redirectUrl
    } catch (error) {
      console.error('Failed to redirect to shop:', error)
      alert('Failed to redirect to shop. Please try again.')
      setIsLoadingSSO(false)
    }
  }
  
  return (
    <div className="card bg-base-100 shadow-xl">
      <div className="card-body">
        <div className="flex items-center gap-6 flex-wrap justify-center">
          {/* Avatar */}
          <div className="relative">
            <div className="avatar placeholder">
              <div className="bg-gradient-to-br from-primary to-secondary text-primary-content rounded-full w-24 h-24 ring ring-primary ring-offset-base-100 ring-offset-2">
                {avatarUrl ? (
                  <img 
                    src={avatarUrl} 
                    alt={`${user.username || 'User'}'s avatar`}
                    className="w-full h-full object-cover rounded-full"
                  />
                ) : (
                  <span className="text-4xl select-none">{user.username?.charAt(0)?.toUpperCase() || "?"}</span>
                )}
              </div>
            </div>
            
            {/* Edit Avatar Button */}
            {onEditAvatar && (
              <button
                onClick={onEditAvatar}
                className="absolute -bottom-2 -right-2 btn btn-sm btn-circle btn-primary"
                title="Edit Avatar"
              >
                <FaEdit className="text-xs" />
              </button>
            )}
          </div>
          
          {/* User Info */}
          <div className="flex-1">
            <h1 className="text-3xl font-bold">{user.username || 'User'}</h1>
            <p className="text-base-content/70">{user.email}</p>
          </div>

          {/* Quick Stats */}
          <div className="stats stats-vertical lg:stats-horizontal shadow">
            {/* P6: spendable balance = live merged KIX from the Hellokix shop (includes
                classroom KIX). "—" when the shop is unreachable or the user isn't linked. */}
            <div className="stat">
              <div className="stat-figure text-primary">
                <span className="text-2xl" aria-hidden="true">🪙</span>
              </div>
              <div className="stat-title">KIX Points</div>
              <div className="stat-value text-primary">
                {kix?.available ? kix.kixBalance : '—'}
              </div>
            </div>

            <div className="stat">
              <div className="stat-figure text-secondary">
                <FaNewspaper className="text-2xl" />
              </div>
              <div className="stat-title">Articles</div>
              <div className="stat-value text-secondary">{stats?.articlesRead || 0}</div>
            </div>

            <div className="stat">
              <div className="stat-figure text-accent">
                <FaBook className="text-2xl" />
              </div>
              <div className="stat-title">Books</div>
              <div className="stat-value text-accent">{stats?.booksRead || 0}</div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        {user.access_shop !== false && (
          <div className="card-actions justify-end mt-4 max-md:justify-center">
            <button
              onClick={handleGoToShop}
              disabled={isLoadingSSO}
              className="btn btn-primary gap-2"
            >
              {isLoadingSSO ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Redirecting...
                </>
              ) : (
                <>
                  <FaShoppingCart />
                  Go to Shop
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default ProfileHeader